import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useReactiveVar } from '@apollo/client'

import { useFormattedZoneList, useLoading, useServerError } from 'shared/hooks'
import { TRouterParams } from 'shared/types'

import { isEmpty } from 'shared/utils/isEmpty'
import { filterFactSources } from 'shared/utils/factSources'

import { DEFAULT_ENTITIES_LIMIT } from 'shared/consts'
import {
  activeProjectVar,
  countryCodeVar,
  currentUserVar,
  factSourcesVar,
  isShownAddNewFeedVar,
  labelCodeIdVar,
  projectFeedTotalVar
} from 'modulesCommon/store'

import { useRemoveFeed, useUpdateFeed } from 'modules/Feed/graphql/mutations'
import { GET_FEEDS_BY_CAMPAIGN_ID } from 'modules/Feed/graphql/queries'
import { projectFeedFilterVar, projectFeedVar } from 'modules/Feed/store'
import { useZonesList } from 'modulesCommon/serviceHooks'
import { useGetProjectQuery } from '../../../modulesCommon/graphql/queries/getProject.types'

const useProjectFeed = () => {
  const [isFeedLoaded, setLoaded] = useState(false)
  const { id } = useParams<TRouterParams>()
  useGetProjectQuery({
    skip: !id,
    variables: { id: String(id) },
    onCompleted: (data) => {
      activeProjectVar(data?.getCampaign)
    }
  })

  const activeProject = useReactiveVar(activeProjectVar)
  const projectFeed = useReactiveVar(projectFeedVar)
  const projectFeedFilter = useReactiveVar(projectFeedFilterVar)
  const currentUser = useReactiveVar(currentUserVar)
  const currentLabelId = useReactiveVar(labelCodeIdVar)

  const country = useReactiveVar(countryCodeVar)
  const factSources = useReactiveVar(factSourcesVar)
  const projectFeedTotal = useReactiveVar(projectFeedTotalVar)
  const { zoneList } = useZonesList()
  const { countryCodes } = useFormattedZoneList(zoneList, currentLabelId)

  const { mutate: updateFeed, loading: updateFeedLoading } = useUpdateFeed()
  const { mutate: removeFeed, loading: removeFeedLoading } = useRemoveFeed()


  const {
    data: feed,
    loading: feedLoading,
    error: feedError,
    fetchMore,
    refetch
  } = useQuery(GET_FEEDS_BY_CAMPAIGN_ID, {
    skip: !id,
    variables: {
      campaignId: id,
      limit: DEFAULT_ENTITIES_LIMIT,
      offset: 0,
      country,
      ...(projectFeedFilter.length && { filter: projectFeedFilter })
    }
  })
  

  const increaseFeedOffset = useCallback(() => {
    fetchMore && fetchMore({ variables: { offset: projectFeed.length } })
  }, [projectFeed, fetchMore])

  const factSourcesList = useMemo(() => filterFactSources(factSources, country), [factSources, country])

  useEffect(() => {
    refetch && refetch()
  }, [projectFeedFilter, id, country, refetch])

  useEffect(() => {
    if (feed) {
      projectFeedVar(feed.getFeedsByCampaignId.feeds)
      projectFeedTotalVar(feed.getFeedsByCampaignId.total)
      setLoaded(true)
    }
  }, [feed])

  useEffect(() => {
    if (!isEmpty(activeProject) && currentUser) {
      isShownAddNewFeedVar(currentUser?.abilities?.canCreateStory && activeProject?.active)
    }
  }, [currentUser, activeProject])

  useLoading({
    isLoading: feedLoading || updateFeedLoading || removeFeedLoading
  })
  useServerError([feedError])

  return {
    activeProject,
    updateFeed,
    removeFeed,
    increaseFeedOffset,
    projectFeedTotal,
    projectFeed,
    feedLoading,
    isFeedLoaded,
    country,
    factSourcesList,
    countryCodes,
    zoneList,
    currentLabelId,
    refetchFeeds: refetch
  }
}

export default useProjectFeed
